import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Card,
  CardBody,
  Flex,
  Heading,
  Icon,
  SimpleGrid,
  Stack,
  Text,
} from '@chakra-ui/react';
import { UpdatePaymentProof } from '@diamond/shared/types';
import { Button, DropzoneMultiple, TextField } from '@diamond/shared/ui';
import { Add as AddIcon, ReceiptLongOutlined } from '@mui/icons-material';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import { useState } from 'react';
import { DropzoneState } from 'react-dropzone';
import { FieldErrorsImpl, FormProvider, UseFormReturn } from 'react-hook-form';

interface PaymentProofUploaderProps {
  form: UseFormReturn<UpdatePaymentProof>;
  onSubmit: (values: UpdatePaymentProof) => Promise<void> | void;
  onRemoveFile: (index: number) => void;
  isSubmitting: boolean;
  existingProofs?: Array<{ url: string }>;
  onPreviewExistingProof?: (url: string) => void;
  totalFilesCount?: number;
  isAccordionOpen?: boolean;
  onAccordionChange?: (isOpen: boolean) => void;
}

export const PaymentProofUploader = ({
  form,
  onSubmit,
  onRemoveFile,
  isSubmitting,
  existingProofs = [],
  onPreviewExistingProof,
  totalFilesCount = 0,
  isAccordionOpen,
  onAccordionChange,
}: PaymentProofUploaderProps) => {
  const bankNameLength = form.watch('bank_name')?.length ?? 0;
  const bankAccountLength = form.watch('bank_account')?.length ?? 0;
  const [dropzoneOpen, setDropzoneOpen] = useState<(() => void) | null>(null);
  const [internalAccordionOpen, setInternalAccordionOpen] = useState(false);
  const resolvedAccordionOpen =
    typeof isAccordionOpen === 'boolean'
      ? isAccordionOpen
      : internalAccordionOpen;

  const handleDropzoneInit = (dropzoneState: DropzoneState) => {
    setDropzoneOpen(() => dropzoneState.open);
  };

  const handleAccordionChange = (index: number | number[]) => {
    const isOpen = Array.isArray(index) ? index.includes(0) : index === 0;
    if (typeof isAccordionOpen !== 'boolean') {
      setInternalAccordionOpen(isOpen);
    }
    onAccordionChange?.(isOpen);
  };

  return (
    <FormProvider {...form}>
      <Card
        shadow="none"
        borderRadius="8px"
        borderWidth="1px"
        borderColor="#D9D9D9"
      >
        <Box>
          <CardBody p={0} width="auto">
            <Accordion
              allowToggle
              index={resolvedAccordionOpen ? [0] : []}
              onChange={handleAccordionChange}
            >
              <AccordionItem border="none">
                <AccordionButton
                  w={{ base: '100%', md: '378px' }}
                  h="80px"
                  p="24px"
                  _hover={{ bg: 'gray.50' }}
                  data-testid="accordion-button-payment-proof"
                >
                  <Flex alignItems="center" gap="2" flex={1}>
                    <Icon
                      as={ReceiptLongOutlined}
                      color="blue"
                      boxSize="32px"
                    />
                    <Heading
                      fontSize={{ base: 'lg', md: 'xl' }}
                      fontWeight="600"
                    >
                      Bukti Pembayaran
                    </Heading>
                  </Flex>
                  <AccordionIcon />
                </AccordionButton>

                <AccordionPanel
                  px={{ base: 3, md: 4 }}
                  pb={{ base: 3, md: 4 }}
                  pt={0}
                >
                  <Stack
                    as="form"
                    id="submit-payment-proof"
                    style={{ scrollMarginBlockStart: '10rem' }}
                    onSubmit={form.handleSubmit(onSubmit)}
                    spacing={{ base: 3, md: 4 }}
                  >
                    {/* Warning Badge */}
                    <Flex
                      gap="2"
                      alignItems="center"
                      bg="#FEF3C7"
                      px={{ base: 2, md: 3 }}
                      py="2"
                      borderRadius="4px"
                    >
                      <Icon as={InfoOutlined} color="#F97316" flexShrink={0} />
                      <Text fontSize="xs">
                        Segera unggah bukti pembayaran agar PO diproses
                      </Text>
                    </Flex>

                    {/* Divider */}
                    <Stack pb={{ base: 3, md: 4 }}>
                      <Box
                        w="full"
                        h="1px"
                        bg="repeating-linear-gradient(to right, #D9D9D9 0 6px, transparent 6px 12px)"
                      />
                    </Stack>

                    {/* Conditional: Show existing proofs or editable form */}
                    {existingProofs.length > 0 ? (
                      <>
                        {/* Bank info read-only display - single column layout */}
                        <Stack spacing={{ base: 3, md: 4 }}>
                          <Box>
                            <Text fontSize="sm" color="gray.500" mb={2}>
                              Nama Bank
                            </Text>
                            <Text fontSize="md" fontWeight="semibold">
                              {form.watch('bank_name') || '-'}
                            </Text>
                          </Box>
                          <Box>
                            <Text fontSize="sm" color="gray.500" mb={2}>
                              Nama Pemilik Rekening
                            </Text>
                            <Text fontSize="md" fontWeight="semibold">
                              {form.watch('bank_account') || '-'}
                            </Text>
                          </Box>
                        </Stack>

                        {/* Existing proofs display */}
                        <Stack
                          spacing={{ base: 3, md: 4 }}
                          mt={{ base: 3, md: 4 }}
                        >
                          <Text fontSize="md" color="gray.75">
                            Bukti Transfer
                          </Text>
                          <SimpleGrid
                            columns={{ base: 3, md: 4, xl: 5 }}
                            gap={4}
                            maxW="full"
                          >
                            {existingProofs.map((item, index) => {
                              const fileName =
                                item?.url.length > 10
                                  ? `${item?.url.slice(-10)}`
                                  : item?.url;

                              return (
                                <Stack
                                  key={`${item.url}-${index}`}
                                  textAlign="center"
                                  fontSize={{ base: '12px', md: '16px' }}
                                >
                                  <Stack
                                    direction="row"
                                    justifyContent="center"
                                  >
                                    <Box
                                      _hover={{ cursor: 'pointer' }}
                                      onClick={() =>
                                        onPreviewExistingProof?.(item.url)
                                      }
                                    >
                                      <Icon
                                        as={ReceiptLongOutlined}
                                        height={{ base: 10, md: '66px' }}
                                        width={{ base: 10, md: '66px' }}
                                        padding={2}
                                        bgColor="gray.33"
                                        color="blue"
                                        borderRadius="md"
                                        data-testid="receipt-icon"
                                      />
                                    </Box>
                                  </Stack>
                                  <Text fontSize={{ base: '10px', md: '12px' }}>
                                    {fileName}
                                  </Text>
                                </Stack>
                              );
                            })}
                            {/* Tambah File button - only show if below max files */}
                            {existingProofs.length < 5 && (
                              <Stack
                                textAlign="center"
                                cursor="pointer"
                                onClick={() => dropzoneOpen?.()}
                                spacing={1}
                              >
                                <Stack direction="row" justifyContent="center">
                                  <Box
                                    height="64px"
                                    width="64px"
                                    display="flex"
                                    alignItems="center"
                                    justifyContent="center"
                                    border="2px dashed"
                                    borderColor="#000052"
                                    borderRadius="8px"
                                    bg="#7F7FDA"
                                    _hover={{ bg: '#6B6BC4' }}
                                  >
                                    <Icon
                                      as={AddIcon}
                                      color="white"
                                      boxSize="38px"
                                    />
                                  </Box>
                                </Stack>
                                <Text
                                  fontSize="12px"
                                  color="#0000A3"
                                  fontWeight={400}
                                  lineHeight="100%"
                                  letterSpacing="0.4%"
                                  textAlign="center"
                                >
                                  Tambah File
                                </Text>
                              </Stack>
                            )}
                          </SimpleGrid>
                        </Stack>
                      </>
                    ) : (
                      /* Form Fields - Single Column (feature branch layout with responsive spacing) */
                      <Stack spacing={{ base: 3, md: 4 }}>
                        <Box>
                          <TextField
                            name="bank_name"
                            label="Nama Bank"
                            register={form.register}
                            errors={form.formState.errors}
                            placeholder="Masukkan nama bank"
                            border="1px solid"
                            borderColor="gray.33"
                            maxLength={100}
                          />
                          <Box textAlign="right" mt="1">
                            <Text fontSize="sm" color="gray.750">
                              {bankNameLength}/100
                            </Text>
                          </Box>
                        </Box>

                        <Box>
                          <TextField
                            name="bank_account"
                            label="Nama Pemilik Rekening"
                            register={form.register}
                            errors={form.formState.errors}
                            placeholder="Masukkan nama pemilik rekening"
                            border="1px solid"
                            borderColor="gray.33"
                            maxLength={100}
                          />
                          <Box textAlign="right" mt="1">
                            <Text fontSize="sm" color="gray.750">
                              {bankAccountLength}/100
                            </Text>
                          </Box>
                        </Box>
                      </Stack>
                    )}
                    {/* DropzoneMultiple for file upload handling when files exist */}
                    {existingProofs.length > 0 && existingProofs.length < 5 && (
                      <Box
                        visibility="hidden"
                        position="absolute"
                        width="1px"
                        height="1px"
                        overflow="hidden"
                      >
                        <DropzoneMultiple
                          key={`dropzone-${existingProofs.length}`}
                          name="bukti_bayar"
                          accept={{
                            'application/pdf': ['.pdf'],
                            'image/*': ['.png', '.jpeg', '.jpg'],
                          }}
                          maxSize={5}
                          maxFiles={5 - existingProofs.length}
                          onRemoveFile={onRemoveFile}
                          onDropzoneInit={handleDropzoneInit}
                          errors={
                            form.formState.errors as Partial<
                              FieldErrorsImpl<{ [x: string]: string }>
                            >
                          }
                          isCentered
                        />
                      </Box>
                    )}

                    {existingProofs.length >= 5 ? (
                      <Flex
                        direction="column"
                        align="center"
                        justify="center"
                        p={6}
                        bg="blue.50"
                        borderRadius="md"
                        gap={2}
                      >
                        <Text
                          fontSize="md"
                          fontWeight="semibold"
                          color="blue.600"
                        >
                          Maksimal 5 file telah terupload
                        </Text>
                        <Text fontSize="sm" color="gray.600">
                          File terupload: {existingProofs.length}/5
                        </Text>
                      </Flex>
                    ) : (
                      <>
                        {/* Only show full DropzoneMultiple when no existing proofs */}
                        {existingProofs.length === 0 && (
                          <DropzoneMultiple
                            key={`dropzone-${existingProofs.length}`}
                            name="bukti_bayar"
                            accept={{
                              'application/pdf': ['.pdf'],
                              'image/*': ['.png', '.jpeg', '.jpg'],
                            }}
                            maxSize={5}
                            maxFiles={5 - existingProofs.length}
                            onRemoveFile={onRemoveFile}
                            errors={
                              form.formState.errors as Partial<
                                FieldErrorsImpl<{ [x: string]: string }>
                              >
                            }
                            isCentered
                          />
                        )}
                        <Flex
                          justifyContent="space-between"
                          alignItems="center"
                        >
                          <Text fontSize="sm" textColor="gray.75">
                            Hanya menerima format PDF, PNG, JPG dengan ukuran
                            maksimal 5 MB
                          </Text>
                          {existingProofs.length > 0 && (
                            <Text
                              fontSize="sm"
                              fontWeight="medium"
                              color="blue.600"
                            >
                              File terupload: {existingProofs.length}/5
                            </Text>
                          )}
                        </Flex>
                        <Button
                          display={{ base: 'inline-flex' }}
                          variant="solid"
                          type="submit"
                          width="200px"
                          alignSelf="flex-end"
                          isLoading={isSubmitting}
                        >
                          <span>Kirim</span>
                        </Button>
                      </>
                    )}
                  </Stack>
                </AccordionPanel>
              </AccordionItem>
            </Accordion>
          </CardBody>
        </Box>
      </Card>
    </FormProvider>
  );
};
